import { cn } from '@/lib/cn';

/**
 * Skeleton — la forma de lo que viene, no un spinner girando.
 *
 * En la auditoría había **41 spinners frente a 10 esqueletos**. Un spinner dice
 * «espera» y nada más; un esqueleto dice «va a haber una tabla de cinco filas
 * aquí», así que el ojo ya coloca el contenido antes de que llegue y la
 * espera se siente más corta aunque dure lo mismo.
 *
 * Regla de uso: el esqueleto tiene que parecerse a lo que sustituye. Si la
 * lista real trae tres líneas por fila, el esqueleto trae tres. Un esqueleto
 * genérico es un spinner con otra forma.
 */
export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn('animate-pulse rounded-xs bg-sunken', className)}
      // Se anuncia una sola vez, no por cada barra: si cada rectángulo fuera
      // `aria-busy`, el lector de pantalla leería «cargando» quince veces.
      aria-hidden="true"
    />
  );
}

/** Filas de tabla en carga. `columnas` debe coincidir con la tabla real. */
export function SkeletonFilas({ filas = 5, columnas = 4 }: { filas?: number; columnas?: number }) {
  return (
    <div role="status" aria-live="polite" aria-label="Cargando datos">
      {Array.from({ length: filas }).map((_, f) => (
        <div key={f} className="flex gap-4 border-b border-line px-4 py-3">
          {Array.from({ length: columnas }).map((_, c) => (
            // La primera columna más ancha: en casi todas las tablas del portal
            // es el nombre o el radicado, y las demás son datos cortos.
            <Skeleton key={c} className={cn('h-4', c === 0 ? 'w-2/5' : 'flex-1')} />
          ))}
        </div>
      ))}
      <span className="sr-only">Cargando datos…</span>
    </div>
  );
}

/** Tarjetas en carga, para las rejillas (cursos, eventos, recursos). */
export function SkeletonTarjetas({ n = 6 }: { n?: number }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(268px,1fr))]"
    >
      {Array.from({ length: n }).map((_, i) => (
        <div key={i} className="rounded-md border border-line bg-surface p-4">
          <Skeleton className="mb-3 h-28 w-full rounded-sm" />
          <Skeleton className="mb-2 h-4 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      ))}
      <span className="sr-only">Cargando…</span>
    </div>
  );
}
